import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { blocksOf, listEdits, type Edit } from "./proof-edits";
import { EditorTour, tourSeen, type TourStep } from "./proof-tour";
import {
  checkProofLimits,
  type EmailStatus,
  type ProofEditResponse,
  type ProofEditSuccess,
  type ProofLimitRules,
  type ProofNoteText,
  type ProofRecord,
  type ProofTextPiece,
  type ProofTextResponse,
  type ProofLookupFailure,
  sanitizeProofText,
} from "./types";

// ── Text <-> editor ──────────────────────────────────────────────────────────
// The stored format is plain text: "\n" is a line break, a blank line starts a new stanza or paragraph, <i>…</i> / <b>…</b> mark
// italics and bold, and [^n] is the mark of footnote n. In the editor each paragraph (or stanza) is its own box, every Return is
// a line break, and a footnote mark is a small number that can't be typed over (it is turned back into [^n] when saved).

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function textToEditorHtml(text: string): string {
  // Escape everything, then let our own two tags and the footnote marks back through.
  const escaped = escapeHtml(text.replace(/\r/g, "").trim())
    .replace(/&lt;(\/?)([ib])&gt;/gi, (_m, slash: string, tag: string) => `<${slash}${tag.toLowerCase()}>`)
    .replace(/\[\^(\d{1,3})\]/g, (_m, n: string) => `<sup class="proof-fn" contenteditable="false" data-fn="${n}" title="Footnote ${n}">${n}</sup>`);
  return escaped.split("\n").join("<br>");
}

const BLOCKS = new Set(["DIV", "P", "LI", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6"]);

export function editorToText(root: HTMLElement): string {
  let out = "";
  const newline = () => { if (out && !out.endsWith("\n")) out += "\n"; };
  const walk = (node: Node, italic: boolean, bold: boolean) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // The editor is white-space: pre-wrap, so browsers may store Return as a literal "\n" in the text rather than a <br>.
      const t = (node.textContent ?? "").replace(/ /g, " ");
      if (!t) return;
      out += t.split("\n").map((part) => {
        if (!part) return part;
        let s = part;
        if (bold) s = `<b>${s}</b>`;
        if (italic) s = `<i>${s}</i>`;
        return s;
      }).join("\n");
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === "BR") { out += "\n"; return; }
    if (node.dataset.fn) { out += `[^${node.dataset.fn}]`; return; }        // a footnote mark
    const style = node.style;
    const i = italic || node.tagName === "I" || node.tagName === "EM" || style.fontStyle === "italic";
    const b = bold || node.tagName === "B" || node.tagName === "STRONG" || style.fontWeight === "bold" || Number(style.fontWeight) >= 600;
    const block = BLOCKS.has(node.tagName);
    if (block) newline();
    node.childNodes.forEach((child) => walk(child, i, b));
    if (block) newline();
  };
  root.childNodes.forEach((child) => walk(child, false, false));
  return sanitizeProofText(out.replace(/<\/i><i>/g, "").replace(/<\/b><b>/g, ""));
}

type Draft = { title: string; text: string; notes?: ProofNoteText[] };

// ── One piece: its title, a box per paragraph (or stanza), and its footnotes ─────────────────

function PieceEditor({
  piece,
  value,
  resetKey,
  disabled,
  onChange,
  onFocusBlock,
  onUndo,
  theirs,
}: {
  /** The editor's page: "The poem", not "Your poem". */
  theirs?: boolean;
  piece: ProofTextPiece;
  value: Draft;
  resetKey: number;
  disabled: boolean;
  onChange: (next: Draft) => void;
  onFocusBlock: (el: HTMLDivElement) => void;
  onUndo: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Uncontrolled on purpose: React re-rendering a contentEditable loses the caret. The boxes are rebuilt only on a reset (Undo,
  // Join, Split, Undo all), from the draft.
  const [blocks, setBlocks] = useState<string[]>(() => blocksOf(value.text));
  const [title, setTitle] = useState(value.title);
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    setBlocks(blocksOf(valueRef.current.text));
    setTitle(valueRef.current.title);
  }, [resetKey]);
  useEffect(() => {
    wrapRef.current?.querySelectorAll<HTMLDivElement>(".proof-blk").forEach((el, i) => { el.innerHTML = textToEditorHtml(blocks[i] ?? ""); });
    wrapRef.current?.querySelectorAll<HTMLDivElement>(".proof-note-text").forEach((el) => {
      const n = Number(el.dataset.n);
      el.innerHTML = textToEditorHtml((valueRef.current.notes ?? []).find((x) => x.n === n)?.text ?? "");
    });
  }, [blocks]);

  const collect = (nextTitle = title): Draft => {
    const els = wrapRef.current ? [...wrapRef.current.querySelectorAll<HTMLDivElement>(".proof-blk")] : [];
    const text = els.map((el) => editorToText(el)).filter((t) => t.trim()).join("\n\n");
    const notes = (value.notes ?? []).map((x) => {
      const el = wrapRef.current?.querySelector<HTMLDivElement>(`.proof-note-text[data-n="${x.n}"]`);
      return el ? { n: x.n, text: editorToText(el).replace(/\n+/g, " ") } : x;
    });
    return { title: nextTitle, text, ...(value.notes?.length ? { notes } : {}) };
  };
  const emit = (nextTitle = title) => onChange(collect(nextTitle));
  const unit = piece.genre === "poetry" ? "stanza" : "paragraph";

  const keys = (event: React.KeyboardEvent<HTMLDivElement>, single = false) => {
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "z") { event.preventDefault(); onUndo(); return; }
    if (event.key === "Enter") {
      event.preventDefault();
      if (!single) { document.execCommand("insertLineBreak"); emit(); }
    }
  };
  const paste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // Plain text only, so pasted fonts and colours can't sneak in.
    event.preventDefault();
    document.execCommand("insertText", false, event.clipboardData.getData("text/plain").replace(/\r\n?/g, "\n"));
    emit();
  };

  return (
    <section className="proof-edit-piece" ref={wrapRef} data-piece={piece.pieceId}>
      <label className="proof-label" htmlFor={`proof-edit-title-${piece.pieceId}`}>Title</label>
      <input
        id={`proof-edit-title-${piece.pieceId}`}
        className="proof-input proof-edit-title"
        value={title}
        disabled={disabled}
        maxLength={200}
        onChange={(event) => { setTitle(event.target.value); emit(event.target.value); }}
      />
      <span className="proof-label">{theirs ? "The" : "Your"} {piece.genre === "poetry" ? "poem" : "piece"}</span>
      <div className={`proof-blocks proof-blocks--${piece.genre}`}>
        {blocks.map((_b, i) => (
          <div
            key={`${resetKey}-${i}`}
            className="proof-blk"
            data-blk={`${piece.pieceId}:${i}`}
            contentEditable={!disabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label={`${unit} ${i + 1}`}
            spellCheck
            onFocus={(event) => onFocusBlock(event.currentTarget)}
            onInput={() => emit()}
            onKeyDown={(event) => keys(event)}
            onPaste={paste}
            onDrop={(event) => event.preventDefault()}
          />
        ))}
      </div>
      {(value.notes ?? []).length > 0 && (
        <div className="proof-fn-list">
          <span className="proof-label">Footnotes</span>
          {(value.notes ?? []).map((x) => (
            <div className="proof-note" key={x.n} data-note={`${piece.pieceId}:${x.n}`}>
              <span className="proof-note-n">{x.n}</span>
              <div
                className="proof-note-text"
                data-n={x.n}
                contentEditable={!disabled}
                suppressContentEditableWarning
                role="textbox"
                aria-label={`Footnote ${x.n}`}
                spellCheck
                onFocus={(event) => onFocusBlock(event.currentTarget)}
                onInput={() => emit()}
                onKeyDown={(event) => keys(event, true)}
                onPaste={paste}
              />
            </div>
          ))}
          <p className="proof-hint">Correct a footnote's words here. Its number in the text can't be typed over; to move or drop a footnote, say so in your note to the editor.</p>
        </div>
      )}
    </section>
  );
}

// ── My edits ──────────────────────────────────────────────────────────────────

const LABEL: Record<Edit["kind"], string> = { title: "Title", words: "Words", added: "Added", removed: "Taken out", split: "Split", joined: "Joined", note: "Footnote" };

function MyEdits({ edits, onGo }: { edits: Edit[]; onGo: (e: Edit) => void }) {
  if (!edits.length) return <p className="proof-body">You haven't changed anything yet. Every change you make shows up here, with a link to it.</p>;
  const groups: { title: string; items: Edit[] }[] = [];
  for (const e of edits) {
    const g = groups[groups.length - 1];
    if (g && g.items[0]!.pieceId === e.pieceId) g.items.push(e); else groups.push({ title: e.pieceTitle, items: [e] });
  }
  return (
    <div className="my-edits">
      <p className="proof-hint">Everything you've changed so far. Click one to go to it.</p>
      {groups.map((g) => (
        <section key={g.items[0]!.id}>
          <h3 className="my-edits-title">{g.title}</h3>
          <ul>
            {g.items.map((e) => (
              <li key={e.id}>
                <button type="button" className="my-edit" onClick={() => onGo(e)}>
                  <span className={`my-edit-kind k-${e.kind}`}>{LABEL[e.kind]}</span>
                  <span className="my-edit-body">
                    <span>{e.text}</span>
                    {e.diff && <span className="my-edit-diff" dangerouslySetInnerHTML={{ __html: e.diff }} />}
                  </span>
                  <span className="my-edit-go">Go to it →</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ── The whole editing panel ─────────────────────────────────────────────────

type Loaded = { original: ProofTextPiece[]; limits: ProofLimitRules };
const draftOf = (p: ProofTextPiece): Draft => ({ title: p.title, text: p.text, ...(p.notes?.length ? { notes: p.notes } : {}) });
const sameNotes = (a: ProofNoteText[] = [], b: ProofNoteText[] = []) => a.length === b.length && a.every((x, k) => x.n === b[k]!.n && x.text.trim() === b[k]!.text.trim());

/** For the editor's final proof page: the same editor on his own routes and in his words. */
export interface EditorMode {
  textUrl: string;
  editUrl: string;
  /** Sent with every request (which item he's editing). */
  extra: Record<string, unknown>;
  heading: string;
  intro: string;
  noteLabel: string;
  sendLabel: string;
  savedText: string;
  tourSteps: TourStep[];
  tourPrefix: string;
}

export function ProofEditPanel({
  proof,
  onCancel,
  onSent,
  onClosed,
  editor,
}: {
  proof: Pick<ProofRecord, "token">;
  onCancel: () => void;
  onSent: (emailStatus: EmailStatus, duplicate: boolean) => void;
  onClosed: () => void;
  /** Set on the editor's page: his routes and wording; the page limit warns but doesn't stop him. */
  editor?: EditorMode;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<ProofEditSuccess | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [view, setView] = useState<"edit" | "edits">("edit");
  const [touring, setTouring] = useState(false);
  const attemptRef = useRef<{ fingerprint: string; id: string } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastBox = useRef<HTMLDivElement | null>(null);
  // Undo: the drafts before each burst of typing (a pause of a second starts a new step), and before each Join or Split.
  const history = useRef<Record<string, Draft>[]>([]);
  const lastPush = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(editor?.textUrl ?? "/api/proof/text", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: proof.token, ...(editor?.extra ?? {}) }),
        });
        const data = (await res.json()) as ProofTextResponse | ProofLookupFailure;
        if (!live) return;
        if (!data.ok) { setLoadError(data.errors[0] ?? "Editing isn't available right now."); return; }
        setLoaded({ original: data.text.pieces, limits: data.limits });
        setDraft(Object.fromEntries(data.text.pieces.map((p) => [p.pieceId, draftOf(p)])));
      } catch {
        if (live) setLoadError("We couldn't reach the server. Please try again.");
      }
    })();
    return () => { live = false; };
  }, [proof.token]);

  useEffect(() => {
    if (!loaded) return;
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (!tourSeen(proof.token, editor?.tourPrefix)) window.setTimeout(() => setTouring(true), 500);   // first time: the walkthrough
  }, [loaded, proof.token, editor?.tourPrefix]);

  const remember = useCallback((force = false) => {
    const now = Date.now();
    if (force || now - lastPush.current > 1000) {
      history.current.push(JSON.parse(JSON.stringify(draftRef.current)) as Record<string, Draft>);
      if (history.current.length > 200) history.current.shift();
    }
    lastPush.current = now;
  }, []);
  const change = (pieceId: string, next: Draft) => { remember(); setDraft((d) => ({ ...d, [pieceId]: next })); };
  const undo = () => {
    const prev = history.current.pop();
    if (!prev) return;
    lastPush.current = 0;
    setDraft(prev);
    setResetKey((k) => k + 1);
  };

  // Join the paragraph the cursor is in onto the one above; split it in two where the cursor is.
  const focusedBlock = (): { pieceId: string; index: number; el: HTMLDivElement } | null => {
    const el = lastBox.current;
    const tag = el?.dataset.blk;
    if (!el || !tag || !document.body.contains(el)) return null;
    const cut = tag.lastIndexOf(":");
    return { pieceId: tag.slice(0, cut), index: Number(tag.slice(cut + 1)), el };
  };
  const join = () => {
    const f = focusedBlock();
    if (!f) { setError("Click into the paragraph you want to join onto the one above, then press Join."); return; }
    if (f.index === 0) { setError("That's the first one: there's nothing above it to join onto."); return; }
    const p = loaded?.original.find((x) => x.pieceId === f.pieceId);
    const d = draftRef.current[f.pieceId];
    if (!p || !d) return;
    const blocks = blocksOf(d.text);
    blocks.splice(f.index - 1, 2, `${blocks[f.index - 1]}${p.genre === "poetry" ? "\n" : " "}${blocks[f.index]}`);
    remember(true);
    setError(null);
    setDraft((all) => ({ ...all, [f.pieceId]: { ...d, text: blocks.join("\n\n") } }));
    setResetKey((k) => k + 1);
  };
  const split = () => {
    const f = focusedBlock();
    const sel = window.getSelection();
    if (!f || !sel || !sel.rangeCount || !f.el.contains(sel.anchorNode)) { setError("Click where the paragraph should break in two, then press Split."); return; }
    const range = sel.getRangeAt(0);
    const head = document.createRange(); head.selectNodeContents(f.el); head.setEnd(range.startContainer, range.startOffset);
    const tail = document.createRange(); tail.selectNodeContents(f.el); tail.setStart(range.endContainer, range.endOffset);
    const asText = (r: Range) => { const box = document.createElement("div"); box.appendChild(r.cloneContents()); return editorToText(box).trim(); };
    const a = asText(head), b = asText(tail);
    if (!a || !b) { setError("Put the cursor between two words, with words on both sides, then press Split."); return; }
    const d = draftRef.current[f.pieceId];
    if (!d) return;
    const blocks = blocksOf(d.text);
    blocks.splice(f.index, 1, a, b);
    remember(true);
    setError(null);
    setDraft((all) => ({ ...all, [f.pieceId]: { ...d, text: blocks.join("\n\n") } }));
    setResetKey((k) => k + 1);
  };
  const format = (cmd: "italic" | "bold") => {
    lastBox.current?.focus();
    document.execCommand(cmd);
    lastBox.current?.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const edits = useMemo(
    () => (loaded ? listEdits(loaded.original.map((p) => ({ pieceId: p.pieceId, title: p.title, text: p.text, notes: p.notes })),
      Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, { pieceId: k, ...v }])), loaded.original.some((p) => p.genre === "poetry") ? "stanza" : "paragraph") : []),
    [loaded, draft],
  );
  const goTo = (e: Edit) => {
    setView("edit");
    window.setTimeout(() => {
      const sel = !e.target ? `[data-piece="${CSS.escape(e.pieceId)}"]`
        : "block" in e.target ? `[data-blk="${CSS.escape(`${e.pieceId}:${e.target.block}`)}"]` : `[data-note="${CSS.escape(`${e.pieceId}:${e.target.note}`)}"]`;
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("proof-flash"); void el.offsetWidth; el.classList.add("proof-flash");
    }, 60);
  };

  if (saved) {
    return (
      <div className="proof-edit" ref={panelRef}>
        <h2 className="proof-edit-heading">{editor ? "Sent to Baz" : "Your saved changes"}</h2>
        <p className="proof-body" role="status">{editor ? editor.savedText : "These are the titles, text and note received by the editor."}</p>
        {saved.saved.pieces.map((p) => (
          <section className="proof-edit-piece" key={p.pieceId}>
            <h3>{p.title}</h3>
            <div className="proof-edit-body" dangerouslySetInnerHTML={{ __html: textToEditorHtml(p.text) }} />
            {(p.notes ?? []).map((x) => <p key={x.n} className="proof-hint"><b>{x.n}</b> <span dangerouslySetInnerHTML={{ __html: textToEditorHtml(x.text) }} /></p>)}
          </section>
        ))}
        {saved.saved.note && <p className="proof-body" style={{ whiteSpace: "pre-wrap" }}>{saved.saved.note}</p>}
        <button type="button" className="proof-btn proof-btn--ok" onClick={() => onSent(saved.emailStatus, saved.duplicate === true)}>Done</button>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="proof-edit" ref={panelRef}>
        <p className="proof-error" role="alert">{loadError}</p>
        <button type="button" className="proof-link" onClick={onCancel}>Back</button>
      </div>
    );
  }
  if (!loaded) return <p className="proof-status" role="status">Opening your text…</p>;

  const pieces = loaded.original.map((p) => ({
    pieceId: p.pieceId,
    genre: p.genre,
    title: draft[p.pieceId]?.title ?? p.title,
    text: draft[p.pieceId]?.text ?? p.text,
    notes: draft[p.pieceId]?.notes ?? p.notes,
  }));
  const report = checkProofLimits(pieces, loaded.limits);
  const changed = pieces.some((p, i) => p.title.trim() !== loaded.original[i].title.trim() || p.text.trim() !== loaded.original[i].text.trim()
    || !sameNotes(p.notes, loaded.original[i].notes));
  const empty = pieces.some((p) => !p.title.trim() || !p.text.trim());
  const unit = report.genre === "poetry" ? "lines" : "words";
  const left = report.max - report.used;
  const poetry = report.genre === "poetry";

  const blockOver = report.over && !editor;          // the editor may go over a writer's limit; Baz checks the fit
  const send = async () => {
    if (blockOver || empty || (!changed && !note.trim())) return;
    const body = {
      pieces: pieces.map(({ pieceId, title, text, notes }) => ({ pieceId, title: title.trim(), text: text.trim(), ...(notes?.length ? { notes } : {}) })),
      note: note.trim(),
    };
    const fingerprint = JSON.stringify(body);
    if (attemptRef.current?.fingerprint !== fingerprint) attemptRef.current = { fingerprint, id: crypto.randomUUID() };
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(editor?.editUrl ?? "/api/proof/edit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: proof.token, attemptId: attemptRef.current.id, ...(editor?.extra ?? {}), ...body }),
      });
      const data = (await res.json()) as ProofEditResponse;
      if (data.ok) {
        attemptRef.current = null;
        setDraft(Object.fromEntries(data.saved.pieces.map((p) => [p.pieceId, { title: p.title, text: p.text, ...(p.notes?.length ? { notes: p.notes } : {}) }])));
        setNote(data.saved.note);
        setSaved(data);
      } else if (data.reason === "closed") {
        attemptRef.current = null;
        onClosed();
      } else {
        if (res.status === 409) attemptRef.current = null;
        setError(data.errors[0] ?? "We couldn't save your changes. Please try again.");
      }
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="proof-edit" ref={panelRef}>
      <div className="proof-edit-head">
        <h2 className="proof-edit-heading">{editor ? editor.heading : "Make changes"}</h2>
        <button type="button" className="proof-link" onClick={() => { setView("edit"); window.setTimeout(() => setTouring(true), 60); }}>How this works</button>
      </div>
      {editor ? <p className="proof-body">{editor.intro}</p> : (
      <p className="proof-body">
        Change whatever you like. The only rule is that it still fits your page
        {poetry
          ? <> — <strong>{report.max} lines</strong> across your poems, counting the empty lines between stanzas.</>
          : <> — <strong>{report.max} words</strong> per piece.</>}
      </p>)}

      <div className="proof-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={view === "edit"} className={`proof-tab ${view === "edit" ? "on" : ""}`} onClick={() => setView("edit")}>Edit</button>
        <button type="button" role="tab" aria-selected={view === "edits"} className={`proof-tab proof-tab-edits ${view === "edits" ? "on" : ""}`} onClick={() => setView("edits")}>
          My edits{edits.length ? ` (${edits.length})` : ""}
        </button>
      </div>

      {view === "edits" ? <MyEdits edits={edits} onGo={goTo} /> : (
        <>
          <div className="proof-toolbar" role="toolbar" aria-label="Formatting">
            <button type="button" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => format("italic")} title="Italic (select words first) · Cmd/Ctrl+I"><em>I</em></button>
            <button type="button" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => format("bold")} title="Bold (select words first) · Cmd/Ctrl+B"><b>B</b></button>
            <button type="button" disabled={busy || !history.current.length} onMouseDown={(e) => e.preventDefault()} onClick={undo} title="Undo · Cmd/Ctrl+Z">Undo</button>
            <span className="proof-toolbar-sep" />
            <button type="button" className="proof-struct" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={join}
              title={`Pull the ${poetry ? "stanza" : "paragraph"} the cursor is in onto the one above`}>↑ Join with above</button>
            <button type="button" className="proof-struct" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={split}
              title={`Break the ${poetry ? "stanza" : "paragraph"} in two where the cursor is`}>Split here</button>
          </div>
          {loaded.original.map((p) => (
            <PieceEditor
              key={p.pieceId}
              piece={p}
              value={draft[p.pieceId] ?? draftOf(p)}
              resetKey={resetKey}
              disabled={busy}
              onChange={(next) => change(p.pieceId, next)}
              onFocusBlock={(el) => { lastBox.current = el; }}
              onUndo={undo}
              theirs={!!editor}
            />
          ))}
          <p className="proof-hint">
            {poetry ? "Press Return for a new line. Each stanza has its own box." : "Each paragraph has its own box. Return makes a line break inside it."}{" "}
            The boxes are as wide as the printed page, so a line that turns over here turns over in the issue too.{" "}
            Select words and press <em>I</em> or <b>B</b> for italic or bold.
          </p>
        </>
      )}

      <div className={`proof-meter${report.over ? " proof-meter--over" : ""}`} role="status" aria-live="polite">
        <div className="proof-meter-bar" aria-hidden="true">
          <span style={{ width: `${Math.min(100, (report.used / report.max) * 100)}%` }} />
        </div>
        <p>
          {report.over && editor
            ? <><strong>{-left} {unit} over the writer's limit</strong> ({report.max}). You can still send it; Baz will check it fits the page.</>
            : report.over
            ? <><strong>{-left} {unit} over the limit.</strong> Trim it back to {report.max} to send.</>
            : <>{report.used} of {report.max} {unit} used{report.genre === "prose" && pieces.length > 1 ? " (longest piece)" : ""} · {left} to spare</>}
        </p>
      </div>

      <label className="proof-label" htmlFor="proof-edit-note">{editor ? editor.noteLabel : "Anything you want to tell the editor? (optional)"}</label>
      <textarea
        id="proof-edit-note"
        className="proof-textarea"
        rows={3}
        maxLength={2000}
        value={note}
        disabled={busy}
        onChange={(event) => setNote(event.target.value)}
      />

      {error && <p className="proof-error" role="alert">{error}</p>}
      {!changed && !note.trim() && <p className="proof-hint">Nothing has changed yet.</p>}

      <div className="proof-actions">
        <button
          type="button"
          className="proof-btn proof-btn--ok proof-send"
          disabled={busy || blockOver || empty || (!changed && !note.trim())}
          onClick={() => void send()}
        >
          {busy ? "Sending…" : editor ? editor.sendLabel : "Send my changes"}
        </button>
        <button type="button" className="proof-link" disabled={busy || !changed} onClick={() => {
          remember(true);
          setDraft(Object.fromEntries(loaded.original.map((p) => [p.pieceId, draftOf(p)])));
          setResetKey((k) => k + 1);
        }}>
          Undo all my changes
        </button>
        <button type="button" className="proof-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {touring && <EditorTour token={proof.token} onClose={() => setTouring(false)}
        {...(editor ? { steps: editor.tourSteps, prefix: editor.tourPrefix } : {})} />}
    </div>
  );
}
